import authenticationService from "@/services/authenticationService"
import { deleteCookie, getCookie, setCookie } from "@/utils/cookies"
import { ADS_API, API, PM_API, TM_API } from "@/utils/http"

import { SET_BALANCE, SET_USER_DATA } from "../user/mutationTypes"

import { AUTH, LOGOUT, REFRESH_TOKEN } from "./actionTypes"
import {
  AUTH_FAILURE,
  AUTH_LOGOUT,
  AUTH_REQUEST,
  AUTH_SUCCESS,
  REFRESH_TOKEN_FAILURE,
  REFRESH_TOKEN_REQUEST,
  REFRESH_TOKEN_SUCCESS
} from "./mutationTypes"

const state = () => {
  const INITIAL_ACCESS_TOKEN = getCookie("auth_access_mpo") || ""
  const INITIAL_SELLER_UUID = localStorage.getItem("seller-uuid") || ""

  return {
    token: INITIAL_ACCESS_TOKEN,
    status: "", // 'pending' | 'success' | 'error'
    tokenRefreshing: false,
    serverErrors: {},
    sellerUuid: INITIAL_SELLER_UUID
  }
}

const getters = {
  isAuthenticated: (state) => !!state.token
}

const mutations = {
  [AUTH_REQUEST]: (state) => {
    state.status = "pending"
    state.serverErrors = {}
  },

  [AUTH_SUCCESS]: (state, payload) => {
    const { access: accessToken, refresh: refreshToken, expire_in, uuid } = payload
    const expireIn = expire_in ? parseInt(expire_in) * 1000 : 3540 * 1000

    state.sellerUuid = uuid
    state.token = accessToken
    state.status = "success"
    state.serverErrors = {}

    const jwtToken = `Bearer ${accessToken}`

    setCookie("auth_access_mpo", accessToken, expireIn)
    setCookie("auth_refresh_mpo", refreshToken)

    localStorage.setItem("seller-uuid", uuid)

    API.defaults.headers.common["Authorization"] = jwtToken
    ADS_API.defaults.headers.common["Authorization"] = jwtToken
    TM_API.defaults.headers.common["Authorization"] = jwtToken
    PM_API.defaults.headers.common["Authorization"] = jwtToken
  },

  [AUTH_FAILURE]: (state, errors) => {
    state.status = "error"
    state.serverErrors = errors
  },

  [AUTH_LOGOUT]: (state) => {
    state.token = ""

    deleteCookie("auth_access_mpo")
    deleteCookie("auth_refresh_mpo")
    localStorage.removeItem("seller-uuid")

    delete API.defaults.headers.common["Authorization"]
    delete ADS_API.defaults.headers.common["Authorization"]
    delete TM_API.defaults.headers.common["Authorization"]
    delete PM_API.defaults.headers.common["Authorization"]
  },

  [REFRESH_TOKEN_REQUEST]: (state) => {
    state.tokenRefreshing = true

    deleteCookie("auth_access_mpo")

    delete API.defaults.headers.common["Authorization"]
    delete ADS_API.defaults.headers.common["Authorization"]
    delete TM_API.defaults.headers.common["Authorization"]
    delete PM_API.defaults.headers.common["Authorization"]
  },

  [REFRESH_TOKEN_SUCCESS]: (state, payload) => {
    const { access: accessToken, expire_in } = payload
    const expireIn = expire_in ? parseInt(expire_in) * 1000 : 3540 * 1000

    state.token = accessToken

    const jwtToken = `Bearer ${accessToken}`

    setCookie("auth_access_mpo", accessToken, expireIn)

    API.defaults.headers.common["Authorization"] = jwtToken
    ADS_API.defaults.headers.common["Authorization"] = jwtToken
    TM_API.defaults.headers.common["Authorization"] = jwtToken
    PM_API.defaults.headers.common["Authorization"] = jwtToken

    state.tokenRefreshing = false
  },

  [REFRESH_TOKEN_FAILURE]: (state) => {
    state.token = ""
    state.tokenRefreshing = false
  }
}

const actions = {
  async [AUTH]({ commit }, user) {
    commit(AUTH_REQUEST)

    try {
      const response = await authenticationService.signIn(user)
      const { data } = response

      commit(AUTH_SUCCESS, data)

      return Promise.resolve(data)
    } catch (err) {
      commit(AUTH_FAILURE, err?.response?.data || {})

      return Promise.reject(err)
    }
  },

  async [REFRESH_TOKEN]({ commit }, token) {
    commit(REFRESH_TOKEN_REQUEST)

    try {
      const response = await authenticationService.refreshToken(token)
      const { data } = response

      commit(REFRESH_TOKEN_SUCCESS, data)

      return Promise.resolve(data)
    } catch (err) {
      commit(REFRESH_TOKEN_FAILURE, err?.response?.data || {})

      return Promise.reject(err)
    }
  },

  async [LOGOUT]({ commit, state }) {
    try {
      await authenticationService.logout(state.token)
    } catch {
      // Local logout must succeed even when the server token is already invalid or unavailable.
    } finally {
      commit(AUTH_LOGOUT)
      // Reset user data after route meta changes to login page
      commit(`user/${SET_USER_DATA}`, null, { root: true })
      commit(`user/${SET_BALANCE}`, {}, { root: true })
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
